import { CoachingPracticeLine } from '@euler/model/coaching-practice';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { TraineeVoice } from './traineeVoice';
import { PracticeSession } from './usePracticeSession';

/**
 * Sending the trainee's lines: a line spoken over the customer's reply
 * waits until the reply is in; a failed line goes again as it first went,
 * hinted or not and with its voice; and each spoken line keeps how it
 * sounded, by the line it became in the thread.
 */
export function useTraineeSend(
  session: PracticeSession,
  /** Whether a hint was showing, read as a new line goes. */
  takeAfterHint: () => boolean,
) {
  const traineeVoices$ = useCreation(
    () =>
      new BehaviorSubject<ReadonlyMap<CoachingPracticeLine, TraineeVoice>>(
        new Map(),
      ),
    [],
  );
  // How the last line went, for a resend.
  const last = useRef<{ afterHint: boolean; voice: TraineeVoice | null }>({
    afterHint: false,
    voice: null,
  });
  const queued = useRef<{
    line: string;
    afterHint: boolean;
    voice: TraineeVoice | null;
  } | null>(null);

  const deliver = useCallback(
    (line: string, afterHint: boolean, voice: TraineeVoice | null) => {
      last.current = { afterHint, voice };
      const sent = session.send(line, afterHint, voice?.token ?? null);
      if (sent && voice)
        traineeVoices$.next(
          new Map(traineeVoices$.getValue()).set(sent, voice),
        );
    },
    [session, traineeVoices$],
  );

  const send = useCallback(
    (line: string, heard: TraineeVoice | null) => {
      // A failed line sent again, typed or not, goes as it first went:
      // hinted or not, and with the voice it was spoken in.
      const again = line === session.failedLine$.getValue();
      const afterHint = again ? last.current.afterHint : takeAfterHint();
      const voice = heard ?? (again ? last.current.voice : null);
      if (session.phase$.getValue().kind === 'replying') {
        queued.current = { line, afterHint, voice };
        return;
      }
      deliver(line, afterHint, voice);
    },
    [deliver, session, takeAfterHint],
  );

  /** The failed line again, as it first went. */
  const resend = useCallback(
    (line: string) => deliver(line, last.current.afterHint, last.current.voice),
    [deliver],
  );

  useEffect(() => {
    const subscription = session.phase$.subscribe(next => {
      const waiting = queued.current;
      if (!waiting || next.kind === 'replying') return;
      queued.current = null;
      if (next.kind !== 'listening') return;
      deliver(waiting.line, waiting.afterHint, waiting.voice);
    });
    return () => subscription.unsubscribe();
  }, [deliver, session]);

  return useMemo(
    () => ({ send, resend, traineeVoices$ }),
    [send, resend, traineeVoices$],
  );
}
